'use client';

import { SvgIcon } from '@/components/svgIcon';
import SwipeIcon from '@/public/icons/swipe.svg';
import BusinessProductCard from '@/storyblok/businessProductCard/BusinessProductCard';
import type { ComponentBusinessProductCardsStoryblok } from '@/types/component-types-sb';
import classNames from 'classnames';
import { useRef, useState } from 'react';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import { Swiper, type SwiperRef, SwiperSlide } from 'swiper/react';

import classes from './BusinessProductCards.module.scss';

export type BusinessProductCardsSliderProps = {
  blok: ComponentBusinessProductCardsStoryblok;
};

const BusinessProductCardsSlider: React.FC<BusinessProductCardsSliderProps> = ({ blok }) => {
  const [activePage, setActivePage] = useState(1);
  const swiperRef = useRef<SwiperRef>(null);

  const slideNext = () => {
    swiperRef.current?.swiper?.slideNext();
  };

  const slidePrev = () => {
    swiperRef.current?.swiper?.slidePrev();
  };

  return (
    <div>
      <Swiper
        autoHeight={false}
        centeredSlides={true}
        className={classes.swiperRoot}
        draggable={true}
        onSlideChange={(swiper) => setActivePage(swiper.activeIndex + 1)}
        ref={swiperRef}
        slidesPerView="auto"
        spaceBetween={16}
        breakpoints={{
          600: {
            spaceBetween: 40,
            centeredSlides: true,
            slidesPerView: 'auto',
          },
        }}
      >
        {blok.product_cards?.map((product, index) => (
          <SwiperSlide
            key={index}
            className={classNames(classes.slide, { [classes.slideActive!]: index === activePage - 1 })}
          >
            <div className={classes.cardInSwiper}>
              <BusinessProductCard
                blok={product}
                groupLink={blok.link}
                groupFormProductFieldName={blok.formProductFieldName}
                key={index + 1}
              />
            </div>
          </SwiperSlide>
        ))}
      </Swiper>
      <div className={classes.layoutWrapper}>
        <div className={classes.pagination}>
          <div className={classes.bar}>
            <button
              data-testid="product-overview-navigate-left-button"
              aria-label="Nach links ziehen"
              className={classes.toLeft}
              disabled={activePage === 1}
              onClick={() => slidePrev()}
            />
            {blok.product_cards?.map((_, index) => (
              <div
                aria-hidden="true"
                key={index + 1}
                className={classNames(classes.dot, activePage === index + 1 && classes.active)}
              />
            ))}
            <button
              data-testid="product-overview-navigate-right-button"
              aria-label="Nach rechts ziehen"
              className={classes.toRight}
              disabled={activePage === blok.product_cards?.length}
              onClick={() => slideNext()}
            />
          </div>
        </div>
        <SvgIcon component={SwipeIcon} className="color-grey-200" />
      </div>
    </div>
  );
};

export default BusinessProductCardsSlider;
